/**
 * Hook for uploading provider profile images (logo/banner) to R2
 *
 * Uses the presign flow:
 * 1. Request presigned URL from API
 * 2. Upload directly to R2
 * 3. Update profile with new URL
 */
import { authenticatedFetch } from "@lumi/data";
import { useState } from "react";
import { useAuth } from "../../../auth/AuthContext";
import { createRefreshCallback } from "../../../auth/refreshCallback";

interface PresignedUrlResponse {
  uploadUrl: string;
  key: string;
  r2Key: string; // r2:// prefixed key for storage in DB
  expiresAt: string;
}

interface UploadProfileImageParams {
  imageType: "logo" | "logo-square" | "banner";
  file: File;
}

interface UploadProfileImageResult {
  r2Key: string; // r2:// URL for storage in profile - API converts to https:// on read
  key: string;
}

interface UseProfileImageUploadResult {
  uploadImage: (params: UploadProfileImageParams) => Promise<UploadProfileImageResult>;
  uploading: boolean;
  error: string | null;
}

/**
 * Hook for uploading profile images to R2
 * Returns the public URL for updating the provider profile
 */
export function useProfileImageUpload(): UseProfileImageUploadResult {
  const { accessToken, refreshAuth } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const uploadImage = async ({ imageType, file }: UploadProfileImageParams): Promise<UploadProfileImageResult> => {
    if (!accessToken) {
      throw new Error("Not authenticated");
    }

    // Validate file type
    const validTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!validTypes.includes(file.type)) {
      throw new Error("Invalid file type. Please use JPEG, PNG, or WebP.");
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      throw new Error("File too large. Maximum size is 10MB.");
    }

    setUploading(true);
    setError(null);

    try {
      // Step 1: Get presigned URL from API
      const presignedResponse = await authenticatedFetch<PresignedUrlResponse>(
        `${import.meta.env.VITE_API_BASE_URL}/v1/provider/profile/image-presign`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            imageType,
            fileName: file.name,
            fileSize: file.size,
            contentType: file.type,
          }),
        },
        accessToken,
        createRefreshCallback(refreshAuth),
      );

      // Step 2: Upload the file directly to R2
      const uploadResponse = await fetch(presignedResponse.uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": file.type,
        },
        body: file,
      });

      // R2 presigned uploads may return 401 even on success (known Cloudflare behavior)
      if (!uploadResponse.ok && uploadResponse.status !== 401) {
        const errorText = await uploadResponse.text();
        console.error("Profile image upload failed:", errorText);
        throw new Error(`Upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
      }

      return {
        r2Key: presignedResponse.r2Key,
        key: presignedResponse.key,
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Upload failed";
      setError(errorMessage);
      throw err;
    } finally {
      setUploading(false);
    }
  };

  return {
    uploadImage,
    uploading,
    error,
  };
}
